import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { addDays } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { DatePicker, LoaderButton } from '@livable/saas-react-components';
import { Box, Button, Typography } from '@mui/material';
import { SUBSCRIPTION_TERM, getSubscriptionTermLengthInMonths } from 'src/constants/subscriptionTerm';
import { useStore } from 'src/stores';
import { useDebounce } from 'src/utils/hooks/useDebounce';
import { CancellationTimeline } from '../CancellationTimeline';
import { SCHEDULE_DATE_INPUT_FORMAT, formatScheduleDate, formatScheduleDateProse, parseScheduleDate, toScheduleDateOnly } from '../scheduleChangeDates';
import { getScheduleTimelineSummary } from '../scheduleTimeline';
import { getScheduleChangeSubscriptionId } from '../scheduleChangeTypes';
import { CancelCallout } from './CancelCallout';
import { REASON_MAX_LENGTH, ScheduleChangeReasonField } from './ScheduleChangeReasonField';
import { ScheduleChangeLayout } from './layout';
import useStyles from './styles';
const KEY_PREFIX = 'subscriptions.scheduleChange.cancel';
const ESTIMATE_DEBOUNCE_MS = 400;
const buildInitialState = (scheduledDate, scheduledReason, defaultEarliestDate) => ({
  effectiveDate: parseScheduleDate(scheduledDate),
  earliestDate: scheduledDate !== null && scheduledDate !== void 0 ? scheduledDate : defaultEarliestDate,
  reason: scheduledReason !== null && scheduledReason !== void 0 ? scheduledReason : '',
  estimate: null,
  isEarliestDateLoading: !scheduledDate,
  isEstimateLoading: false,
  isSubmitting: false
});
export const CancelScheduleChangeContent = observer(function CancelScheduleChangeContent(_ref) {
  var _scheduledChange$id, _scheduledChange$effe, _scheduledChange$reas, _state$estimate, _rawEstimate$timeline, _currentEstimate$time, _parseScheduleDate;
  let isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    subscription = _ref.subscription,
    scheduledChange = _ref.scheduledChange,
    onScheduled = _ref.onScheduled;
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const store = useStore();
  const subscriptionId = getScheduleChangeSubscriptionId(subscription);
  const changeId = (_scheduledChange$id = scheduledChange === null || scheduledChange === void 0 ? void 0 : scheduledChange.id) !== null && _scheduledChange$id !== void 0 ? _scheduledChange$id : '';
  const scheduledDate = (_scheduledChange$effe = scheduledChange === null || scheduledChange === void 0 ? void 0 : scheduledChange.effectiveFrom) !== null && _scheduledChange$effe !== void 0 ? _scheduledChange$effe : null;
  const scheduledReason = (_scheduledChange$reas = scheduledChange === null || scheduledChange === void 0 ? void 0 : scheduledChange.reason) !== null && _scheduledChange$reas !== void 0 ? _scheduledChange$reas : null;
  const isReschedule = !!changeId;
  const isMonthly = subscription.term === SUBSCRIPTION_TERM.Monthly;
  const clientEarliestDate = toScheduleDateOnly(addDays(new Date(), isMonthly ? 30 : 1));
  const commitmentEnd = parseScheduleDate(subscription.commitmentEndDate);
  const latestDate = !isMonthly && commitmentEnd ? addDays(commitmentEnd, 1) : null;
  const _useState = useState(() => buildInitialState(scheduledDate, scheduledReason, clientEarliestDate)),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const chosenDate = state.effectiveDate ? toScheduleDateOnly(state.effectiveDate) : '';
  const debouncedDate = useDebounce(chosenDate, ESTIMATE_DEBOUNCE_MS);
  useEffect(() => {
    if (!isOpen) return;
    setState(buildInitialState(scheduledDate, scheduledReason, clientEarliestDate));
  }, [isOpen, changeId, scheduledDate, scheduledReason, clientEarliestDate]);
  useEffect(() => {
    if (!isOpen || !subscriptionId || isReschedule) return undefined;
    let isCurrentRequest = true;
    store.stripe.getScheduledChangeEstimateAsync.run({
      subscriptionId,
      type: 'Cancel'
    }, []).then(resp => {
      var _resp$response;
      if (!isCurrentRequest || !(resp !== null && resp !== void 0 && resp.isSuccessful) || !(resp !== null && resp !== void 0 && (_resp$response = resp.response) !== null && _resp$response !== void 0 && _resp$response.effectiveDate)) return;
      setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        earliestDate: resp.response.effectiveDate
      }));
    }).catch(() => undefined).finally(() => {
      if (isCurrentRequest) setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        isEarliestDateLoading: false
      }));
    });
    return () => {
      isCurrentRequest = false;
    };
  }, [isOpen, subscriptionId, isReschedule, store.stripe]);
  useEffect(() => {
    if (!isOpen || !subscriptionId || !debouncedDate) return undefined;
    let isCurrentRequest = true;
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      isEstimateLoading: true
    }));
    store.stripe.getScheduledChangeEstimateAsync.run(_objectSpread({
      subscriptionId,
      type: 'Cancel',
      effectiveDate: debouncedDate
    }, changeId ? {
      scheduledChangeId: changeId
    } : {}), []).then(resp => {
      if (!isCurrentRequest) return;
      setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        estimate: {
          forDate: debouncedDate,
          data: resp !== null && resp !== void 0 && resp.isSuccessful && resp !== null && resp !== void 0 && resp.response ? resp.response : null
        },
        isEstimateLoading: false
      }));
    }).catch(() => {
      if (!isCurrentRequest) return;
      setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        estimate: {
          forDate: debouncedDate,
          data: null
        },
        isEstimateLoading: false
      }));
    });
    return () => {
      isCurrentRequest = false;
    };
  }, [isOpen, subscriptionId, debouncedDate, changeId, store.stripe]);
  const rawEstimate = ((_state$estimate = state.estimate) === null || _state$estimate === void 0 ? void 0 : _state$estimate.forDate) === chosenDate ? state.estimate.data : null;
  const today = toScheduleDateOnly(new Date());
  const currentEstimate = rawEstimate ? _objectSpread(_objectSpread({}, rawEstimate), {}, {
    timeline: ((_rawEstimate$timeline = rawEstimate.timeline) !== null && _rawEstimate$timeline !== void 0 ? _rawEstimate$timeline : []).filter(node => node.kind === 'Final' || node.date >= today && (!subscription.startDate || node.date >= subscription.startDate))
  }) : null;
  const isEstimatePending = state.isEstimateLoading || !!chosenDate && chosenDate !== debouncedDate;
  const isReasonEdited = state.reason.trim() !== (scheduledReason !== null && scheduledReason !== void 0 ? scheduledReason : '').trim();
  /** A reschedule that moves nothing writes a "moved from X to X" activity, so hold it back. */
  const isDirty = !isReschedule || chosenDate !== scheduledDate || isReasonEdited;
  const hasCharge = getScheduleTimelineSummary((_currentEstimate$time = currentEstimate === null || currentEstimate === void 0 ? void 0 : currentEstimate.timeline) !== null && _currentEstimate$time !== void 0 ? _currentEstimate$time : []).hasCharge;
  const handleDateChange = date => {
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      effectiveDate: date
    }));
  };
  const handleReasonChange = event => {
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      reason: event.target.value.slice(0, REASON_MAX_LENGTH)
    }));
  };
  const handleConfirm = async () => {
    if (!subscriptionId || !chosenDate || !isDirty || state.isSubmitting) return;
    const reason = state.reason.trim();
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      isSubmitting: true
    }));
    if (isReschedule) {
      await store.stripe.rescheduleChangeAsync.run(_objectSpread({
        subscriptionId,
        changeId,
        effectiveDate: chosenDate
      }, isReasonEdited ? {
        reason
      } : {}), []);
    } else {
      await store.stripe.scheduleChangeAsync.run(_objectSpread({
        subscriptionId,
        type: 'Cancel',
        effectiveDate: chosenDate
      }, reason ? {
        reason
      } : {}), []);
    }
    const isError = isReschedule ? store.stripe.rescheduleChangeAsync.isError : store.stripe.scheduleChangeAsync.isError;
    setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
      isSubmitting: false
    }));
    if (isError) return;
    if (onScheduled) onScheduled();
    onClose();
  };
  const earliestHelper = (() => {
    if (isReschedule) {
      return scheduledDate ? t("".concat(KEY_PREFIX, ".helper_reschedule"), {
        date: formatScheduleDate(scheduledDate)
      }) : '';
    }
    if (!isMonthly) return t("".concat(KEY_PREFIX, ".helper_earliest_committed"));
    if (!state.earliestDate) return '';
    return t("".concat(KEY_PREFIX, ".helper_earliest_monthly"), {
      date: formatScheduleDateProse(state.earliestDate)
    });
  })();
  const form = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement("fieldset", {
    className: classes.datePickerGate,
    disabled: state.isEarliestDateLoading
  }, /*#__PURE__*/React.createElement(DatePicker, {
    label: t("".concat(KEY_PREFIX, ".date_label")),
    placeholder: t("".concat(KEY_PREFIX, ".date_placeholder")),
    format: SCHEDULE_DATE_INPUT_FORMAT,
    value: state.effectiveDate,
    minDate: (_parseScheduleDate = parseScheduleDate(state.earliestDate)) !== null && _parseScheduleDate !== void 0 ? _parseScheduleDate : undefined,
    maxDate: latestDate !== null && latestDate !== void 0 ? latestDate : undefined,
    onChange: handleDateChange
  }))), !!earliestHelper && /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    className: classes.helper
  }, earliestHelper), /*#__PURE__*/React.createElement(ScheduleChangeReasonField, {
    value: state.reason,
    onChange: handleReasonChange
  }), !!currentEstimate && /*#__PURE__*/React.createElement(CancelCallout, {
    estimate: currentEstimate,
    termMonths: getSubscriptionTermLengthInMonths(subscription.term),
    isMonthly: isMonthly,
    activeUnits: subscription.activeUnitsCount,
    commitmentEndDate: subscription.commitmentEndDate
  }));
  const rail = /*#__PURE__*/React.createElement(CancellationTimeline, {
    startDate: subscription.startDate,
    effectiveDate: chosenDate || null,
    timeline: currentEstimate === null || currentEstimate === void 0 ? void 0 : currentEstimate.timeline,
    isLoading: isEstimatePending
  });
  return /*#__PURE__*/React.createElement(ScheduleChangeLayout, {
    form: form,
    rail: rail,
    footerLeft: /*#__PURE__*/React.createElement(Button, {
      variant: "text",
      onClick: onClose
    }, t("".concat(KEY_PREFIX, ".keep_active"))),
    footerRight: /*#__PURE__*/React.createElement("div", {
      className: hasCharge ? classes.confirmDanger : undefined
    }, /*#__PURE__*/React.createElement(LoaderButton, {
      buttonText: t("".concat(KEY_PREFIX, ".confirm")),
      loadingText: t("".concat(KEY_PREFIX, ".confirm_in_progress")),
      status: state.isSubmitting ? 'loader' : '',
      disabled: !chosenDate || !isDirty || state.isSubmitting || isEstimatePending || !currentEstimate,
      handleClick: handleConfirm
    }))
  });
});